'use client';

import { Button, cx } from '@otter-ai/den-components';
import { CloseIcon } from '@otter-ai/den-icons';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { PATH_URL } from '@/constants/path-constants';
import { useSendPasswordResetMutation } from '@/query/mutations/useSendPasswordResetMutation';
import { EmailInput } from '@/shared/components/auth/EmailInput';
import { TermsAgreementText } from '@/shared/components/TermsAgreementText';
import { useCheckEmailMutation } from '@/shared/hooks/useCheckEmailMutation';
import {
  getSessionItem,
  SESSION_STORAGE_KEYS,
} from '@/shared/utils/sessionStorageUtils';

type FormData = {
  email: string;
};

export default function ForgotPasswordMainPage() {
  const methods = useForm<FormData>({ mode: 'onChange' });
  const {
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid, isSubmitting, isSubmitSuccessful },
  } = methods;
  const t = useTranslations('auth.forgot_password');
  const errorsT = useTranslations('auth.errors');
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(
    null,
  );

  const submitSuccessful = useMemo(
    () => isSubmitSuccessful && !submitErrorMessage,
    [isSubmitSuccessful, submitErrorMessage],
  );

  useEffect(() => {
    const fetchedEmail = getSessionItem<string>(
      SESSION_STORAGE_KEYS.AUTH_EMAIL,
    );

    if (fetchedEmail) {
      setValue('email', fetchedEmail);
      trigger('email');
    } else {
      setValue('email', '');
    }
  }, [setValue, trigger]);

  const { isEmailInUse, isCheckingEmail } = useCheckEmailMutation();
  const {
    mutateAsync: sendPasswordResetAsync,
    isPending: isSendingPasswordReset,
  } = useSendPasswordResetMutation();

  const onSubmit = handleSubmit(async (data) => {
    setSubmitErrorMessage(null);
    const email = data.email;
    try {
      // Should we be checking if email exists here? We may not want to expose if an email is in use or not.
      const emailExists = await isEmailInUse(email);
      if (!emailExists) {
        throw new Error(errorsT('email_not_found'));
      }
      await sendPasswordResetAsync(email);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : errorsT('unexpected_error');
      setSubmitErrorMessage(errorMessage);
    }
  });

  return (
    <main className="flex md:p-5 justify-center w-full items-start">
      <div className="md:mt-24 relative w-full max-w-md rounded-lg md:border md:border-gray-200 bg-white p-4 sm:p-8 md:shadow-md max-w-screen-sm flex flex-col justify-center min-h-96 items-center">
        {!submitSuccessful && (
          <Link
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            aria-label="Close"
            href={`/${PATH_URL.SIGNIN}`}
            data-testid="forgot-password-page-close"
          >
            <CloseIcon className="h-8 w-8 text-primary" size="xl" />
          </Link>
        )}

        <div
          className={cx(
            'max-w-[500px] w-full flex flex-col items-center justify-between',
            submitSuccessful && 'md:mt-12',
          )}
        >
          <h1 className="mb-4 text-center text-3xl font-semibold text-gray-800">
            {submitSuccessful
              ? t('instructions_sent_confirmation')
              : t('forgot_password_prompt')}
          </h1>
          <p className="mb-6 text-subtle ">
            {submitSuccessful
              ? t('instructions_sent_description')
              : t('forgot_password_description')}
          </p>
          {!submitSuccessful && (
            <FormProvider {...methods}>
              <form
                onSubmit={onSubmit}
                className="flex justify-center items-center flex-col w-full"
                noValidate
                data-testid="forgot-password-page-form"
              >
                <div className="w-full mb-1 relative">
                  <EmailInput
                    className="h-14"
                    parentPage="forgot-password"
                    customEmailError={
                      (errors.email?.message || submitErrorMessage) ?? undefined
                    }
                    disabled={
                      isCheckingEmail || isSubmitting || isSendingPasswordReset
                    }
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full md:w-3/4 h-12 my-8 text-lg"
                  disabled={
                    !isValid ||
                    isSubmitting ||
                    isCheckingEmail ||
                    isSendingPasswordReset
                  }
                  data-testid="forgot-password-page-submit"
                >
                  {t('send_reset_link')}
                </Button>
              </form>
            </FormProvider>
          )}
          {submitSuccessful && (
            <Button className="w-full md:w-1/2 h-12 my-8 text-lg">
              <Link href={`/${PATH_URL.SIGNIN}`}>{t('back_to_signin')}</Link>
            </Button>
          )}
        </div>
        <div className="mt-auto">
          <TermsAgreementText />
        </div>
      </div>
    </main>
  );
}
